import React from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Alert, Space, Typography } from 'antd';
import {
    RobotOutlined,
    DollarOutlined,
    UserOutlined,
    ThunderboltOutlined,
    ClockCircleOutlined,
} from '@ant-design/icons';
import { useGetAdminOrganizationTokenUsageByUserQuery, useGetAdminOrganizationMonthlyUsageQuery, useGetAIExpertsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import type { IUserTokenUsage } from '../../services/nestjs-base-api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface OrganizationTokenUsageWidgetProps {
    organizationId: number;
    days?: number;
}

export const OrganizationTokenUsageWidget: React.FC<OrganizationTokenUsageWidgetProps> = ({ 
    organizationId, 
    days = 30 
}) => {
    const { user } = useAuth();
    
    const { data: usageData, isLoading, error } = useGetAdminOrganizationTokenUsageByUserQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organizationId,
        days,
    }, {
        skip: !user?.jwtToken || !organizationId
    });

    const { data: monthlyData, isLoading: monthlyLoading } = useGetAdminOrganizationMonthlyUsageQuery({
        jwtToken: user?.jwtToken || '',
        orgId: organizationId,
        months: 6,
    }, {
        skip: !user?.jwtToken || !organizationId
    });

    // Fetch AI experts data for dynamic names
    const { data: aiExpertsData } = useGetAIExpertsQuery({
        jwtToken: user?.jwtToken || '',
    }, {
        skip: !user?.jwtToken
    });

    if (isLoading) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading token usage data...</div>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <Alert
                    message="Error loading token usage data"
                    description="Unable to fetch token usage statistics. Please try again later."
                    type="warning"
                    showIcon
                    style={{ margin: '20px 0' }}
                />
            </Card>
        );
    }

    if (!usageData) {
        return (
            <Card style={{ marginBottom: 24 }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">No token usage data available</Text>
                </div>
            </Card>
        );
    }

    return <OrganizationTokenUsageDisplay data={usageData} monthlyData={monthlyData} monthlyLoading={monthlyLoading} days={days} aiExpertsData={aiExpertsData} />;
};

const OrganizationTokenUsageDisplay: React.FC<{ data: any; monthlyData: any; monthlyLoading: boolean; days: number; aiExpertsData: any }> = ({ data, monthlyData, monthlyLoading, days, aiExpertsData }) => {
    const formatTokenCount = (tokens: number): string => {
        if (tokens >= 1000000) {
            return `${(tokens / 1000000).toFixed(2)}M`;
        } else if (tokens >= 1000) {
            return `${(tokens / 1000).toFixed(1)}k`;
        }
        return tokens.toString();
    };

    const formatFeatureType = (type: string): string => {
        // Build dynamic types from AI experts data
        const types: Record<string, string> = {
            'element_analysis_measured': 'Measured Elements',
            'element_analysis_latent': 'Latent Elements',
            'ai_experts': 'AI Experts Chat',
        };

        // Add dynamic expert names from API
        if (aiExpertsData) {
            aiExpertsData.forEach((expert: any) => {
                types[`ai_expert_${expert.expertType}`] = expert.name;
                types[`dashboard_ai_expert_${expert.expertType}`] = `Dashboard AI - ${expert.name}`;
            });
        }

        return types[type] || type;
    };

    const getFeatureColor = (type: string): string => {
        const colors: Record<string, string> = {
            'element_analysis_measured': 'blue',
            'element_analysis_latent': 'purple',
            'ai_experts': 'green',
        };

        // Add dynamic colors for experts from API
        if (aiExpertsData) {
            const colorOptions = ['cyan', 'geekblue', 'volcano', 'magenta', 'lime', 'gold'];
            aiExpertsData.forEach((expert: any, index: number) => {
                const color = colorOptions[index % colorOptions.length];
                colors[`ai_expert_${expert.expertType}`] = color;
                colors[`dashboard_ai_expert_${expert.expertType}`] = color;
            });
        }

        return colors[type] || 'default';
    };

    const getRoleColor = (role: string): string => {
        const colors: Record<string, string> = {
            'SuperAdmin': 'red',
            'Admin': 'orange',
            'OrganisationAdmin': 'blue',
            'OrganisationViewer': 'green',
        };
        return colors[role] || 'default';
    };

    const userColumns = [
        {
            title: 'User',
            key: 'user',
            render: (user: IUserTokenUsage) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{user.userName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{user.userEmail}</div>
                    <div style={{ marginTop: 4 }}>
                        {user.userRoles.map(role => (
                            <Tag key={role} color={getRoleColor(role)}>
                                {role}
                            </Tag>
                        ))}
                    </div>
                </div>
            ),
        },
        {
            title: 'Tokens Used',
            dataIndex: 'tokens',
            key: 'tokens',
            sorter: (a: IUserTokenUsage, b: IUserTokenUsage) => a.tokens - b.tokens,
            render: (tokens: number) => (
                <Space>
                    <ThunderboltOutlined style={{ color: '#faad14' }} />
                    <span style={{ fontWeight: 600 }}>{formatTokenCount(tokens)}</span>
                </Space>
            ),
        },
        {
            title: 'Requests',
            dataIndex: 'requests',
            key: 'requests',
            sorter: (a: IUserTokenUsage, b: IUserTokenUsage) => a.requests - b.requests,
        },
        {
            title: 'Features Used',
            key: 'features',
            render: (user: IUserTokenUsage) => (
                <div>
                    {user.featureBreakdown.slice(0, 3).map(feature => (
                        <Tag key={feature.featureType} color={getFeatureColor(feature.featureType)}>
                            {formatFeatureType(feature.featureType)}: {formatTokenCount(feature.tokens)}
                        </Tag>
                    ))}
                </div>
            ),
        },
        {
            title: 'Last Activity',
            dataIndex: 'lastActivity',
            key: 'lastActivity',
            render: (date: Date) => (
                <Space>
                    <ClockCircleOutlined style={{ color: '#52c41a' }} />
                    <span>{dayjs(date).fromNow()}</span>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <RobotOutlined style={{ fontSize: '20px', color: '#F59E0B' }} />
                    <span style={{ fontSize: '16px', fontWeight: 600 }}>AI Token Usage by User</span>
                    <Tag color="blue">Last {days} Days</Tag>
                </div>
            }
            style={{ marginBottom: 24 }}
        >
            {/* Summary Metrics */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Total Tokens</Text>}
                            value={formatTokenCount(data.totalTokens)}
                            prefix={<ThunderboltOutlined style={{ color: '#F59E0B' }} />}
                            valueStyle={{ color: '#F59E0B', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Total Requests</Text>}
                            value={data.totalRequests}
                            prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Estimated Cost</Text>}
                            value={data.totalCost}
                            prefix={<DollarOutlined />}
                            precision={3}
                            valueStyle={{ color: '#fa8c16', fontSize: '20px' }}
                            suffix="USD"
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={8} md={6}>
                    <Card size="small" style={{ backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
                        <Statistic
                            title={<Text style={{ fontSize: '12px' }}>Active Users</Text>}
                            value={data.userBreakdown.length}
                            prefix={<UserOutlined style={{ color: '#722ed1' }} />}
                            valueStyle={{ color: '#722ed1', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* User Breakdown Table */}
            <div style={{ marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>User Breakdown</Title>
            </div>
            
            <Table
                dataSource={data.userBreakdown}
                columns={userColumns}
                pagination={{ pageSize: 50 }}
                size="small"
                rowKey="userId"
                scroll={{ x: 800 }}
                expandable={{
                    expandedRowRender: (user: IUserTokenUsage) => (
                        <div style={{ padding: '16px 0' }}>
                            <Title level={5} style={{ marginBottom: 16 }}>Feature Usage Details</Title>
                            <Row gutter={16}>
                                {user.featureBreakdown.map(feature => (
                                    <Col key={feature.featureType} xs={24} sm={12} md={8} style={{ marginBottom: 12 }}>
                                        <Card size="small">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <Tag color={getFeatureColor(feature.featureType)}>
                                                        {formatFeatureType(feature.featureType)}
                                                    </Tag>
                                                    <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                                                        {feature.requests} requests
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {formatTokenCount(feature.tokens)}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        tokens
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    ),
                    rowExpandable: (user: IUserTokenUsage) => user.featureBreakdown.length > 0,
                }}
            />
            
            {/* Monthly Usage Breakdown */}
            <div style={{ marginTop: 32, marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0 }}>Monthly Breakdown</Title>
            </div>

            {monthlyLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin size="small" />
                </div>
            ) : monthlyData && monthlyData.length > 0 ? (
                <Table
                    dataSource={monthlyData}
                    pagination={false}
                    size="small"
                    rowKey="month"
                    expandable={{
                        expandedRowRender: (record: any) => (
                            <div style={{ padding: '12px 0' }}>
                                <Row gutter={24}>
                                    {record.modelBreakdown.length > 0 && (
                                        <Col xs={24} md={12}>
                                            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>By Model</Text>
                                            {record.modelBreakdown.map((m: any) => (
                                                <div key={m.modelName} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
                                                    <span style={{ color: '#666' }}>{m.modelName}</span>
                                                    <span>
                                                        <span style={{ fontWeight: 500 }}>{formatTokenCount(m.tokens)}</span>
                                                        <span style={{ color: '#999', marginLeft: 8 }}>{m.requests} req</span>
                                                        <span style={{ color: '#fa8c16', marginLeft: 8 }}>${m.cost.toFixed(3)}</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </Col>
                                    )}
                                    {record.featureBreakdown.length > 0 && (
                                        <Col xs={24} md={12}>
                                            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>By Feature</Text>
                                            {record.featureBreakdown.map((f: any) => (
                                                <div key={f.featureType} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
                                                    <Tag color={getFeatureColor(f.featureType)} style={{ fontSize: 11 }}>
                                                        {formatFeatureType(f.featureType)}
                                                    </Tag>
                                                    <span>
                                                        <span style={{ fontWeight: 500 }}>{formatTokenCount(f.tokens)}</span>
                                                        <span style={{ color: '#999', marginLeft: 8 }}>{f.requests} req</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        ),
                        rowExpandable: (record: any) => record.modelBreakdown.length > 0 || record.featureBreakdown.length > 0,
                    }}
                    columns={[
                        {
                            title: 'Month',
                            dataIndex: 'month',
                            key: 'month',
                            render: (month: string) => {
                                const [year, m] = month.split('-');
                                const date = new Date(parseInt(year), parseInt(m) - 1);
                                return <span style={{ fontWeight: 500 }}>{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>;
                            },
                        },
                        {
                            title: 'Tokens',
                            dataIndex: 'totalTokens',
                            key: 'totalTokens',
                            render: (tokens: number) => (
                                <Space>
                                    <ThunderboltOutlined style={{ color: '#faad14' }} />
                                    <span style={{ fontWeight: 600 }}>{formatTokenCount(tokens)}</span>
                                </Space>
                            ),
                        },
                        {
                            title: 'Requests',
                            dataIndex: 'totalRequests',
                            key: 'totalRequests',
                        },
                        {
                            title: 'Est. Cost',
                            dataIndex: 'estimatedCost',
                            key: 'estimatedCost',
                            render: (cost: number) => (
                                <span style={{ color: '#fa8c16', fontWeight: 500 }}>
                                    ${cost.toFixed(2)}
                                </span>
                            ),
                        },
                    ]}
                />
            ) : (
                <Text type="secondary">No monthly usage data available</Text>
            )}
        </Card>
    );
};